import { apiService, createUserAgentString } from "@blacknut/javascript-sdk/dist";
import { DDAppender } from "@blacknut/logging-datadog/dist";
import {
  Appender,
  ConsoleAppender,
  LogLevel,
  OverlayAppender,
  QuickwitAppender,
  logger,
} from "@blacknut/logging/dist";
import { State, StorageKey } from "core";
import { useEffect } from "react";
import { useSelector } from "react-redux";

const QUICKWIT_URL = "https://logs-ingest.blacknut.net:3443/api/v1/blacknut-main/ingest";

export const CONSOLE_APPENDER = new ConsoleAppender();
export const OVERLAY_APPENDER = new OverlayAppender();
export const DD_APPENDER = new DDAppender({ type: "App" });
export const QUICKWIT_APPENDER = new QuickwitAppender({
  type: "App",
  URL: QUICKWIT_URL,
  auth: process.env.REACT_APP_QUICKWIT_AUTH ?? "",
  blkAuth: process.env.REACT_APP_QUICKWIT_BLK_AUTH ?? "",
});

const getEnv = (platform?: string) => {
  if (platform && platform.indexOf("api.blacknut.com") !== -1) {
    return "prod";
  }
  if (platform && platform.indexOf("staging") !== -1) {
    return "staging";
  }
  return "dev";
};

export const useLogging = () => {
  const { config } = useSelector((state: State) => state.globalState);
  const { platform, user } = useSelector((state: State) => state.globalState);

  useEffect(() => {
    const appenders: Appender[] =
      process.env.NODE_ENV === "development"
        ? [CONSOLE_APPENDER, DD_APPENDER]
        : [DD_APPENDER];

    const logsOverlay = localStorage.getItem(StorageKey.LOGS_OVERLAY) === "true";
    if (
      process.env.NODE_ENV === "development" ||
      process.env.NODE_ENV === "test" ||
      logsOverlay
    ) {
      if (!appenders.includes(CONSOLE_APPENDER)) {
        appenders.push(CONSOLE_APPENDER);
      }
      if (logsOverlay && !appenders.includes(OVERLAY_APPENDER)) {
        appenders.push(OVERLAY_APPENDER);
      }
    }

    QUICKWIT_APPENDER.env = getEnv(platform);
    QUICKWIT_APPENDER.user = user?.uuid;
    appenders.push(QUICKWIT_APPENDER);

    logger.configure({
      level:
        process.env.NODE_ENV === "development" || logsOverlay
          ? LogLevel.LEVEL_DEBUG
          : LogLevel.LEVEL_INFO,
      appenders,
    });
  }, [platform, user?.uuid]);

  useEffect(() => {
    switch (config?.userConf?.levelLogClient) {
      case "debug":
      case "trace":
        logger.level = LogLevel.LEVEL_DEBUG;
        logger.configure({
          level: LogLevel.LEVEL_DEBUG,
          appenders: [
            ...(logger.appenders || []).filter((f) => f !== CONSOLE_APPENDER),
            CONSOLE_APPENDER,
          ],
        });
        break;
      case "info":
        logger.level = LogLevel.LEVEL_INFO;
        break;
      case "warn":
        logger.level = LogLevel.LEVEL_WARN;
        break;
    }
  }, [config?.userConf?.levelLogClient]);

  useEffect(() => {
    QUICKWIT_APPENDER.addExtraProperty(
      "blk-user-agent",
      createUserAgentString(apiService.userAgent),
    );
    QUICKWIT_APPENDER.addExtraProperty("device_os", apiService.userAgent.os);

    QUICKWIT_APPENDER.addExtraProperty("device_api", apiService.userAgent.apiVersion);
    QUICKWIT_APPENDER.addExtraProperty(
      "device_apiversion",
      apiService.userAgent.apiVersion,
    );

    QUICKWIT_APPENDER.addExtraProperty("device_codename", apiService.userAgent.client);
    QUICKWIT_APPENDER.addExtraProperty(
      "device_version",
      apiService.userAgent.androidVersion || apiService.userAgent.clientVersion,
    );
    QUICKWIT_APPENDER.addExtraProperty(
      "device.version",
      apiService.userAgent.androidVersion || apiService.userAgent.clientVersion,
    );
    QUICKWIT_APPENDER.addExtraProperty("device_kind", apiService.userAgent.deviceType);
    QUICKWIT_APPENDER.addExtraProperty("device_os", apiService.userAgent.os);
    QUICKWIT_APPENDER.addExtraProperty("device.os", apiService.userAgent.os);

    if (apiService.locale) {
      const matchFull = apiService.locale.match(/([a-zA-Z]{2})-([a-zA-Z]{2})/);
      if (matchFull) {
        QUICKWIT_APPENDER.addExtraProperty("device_country", matchFull[2]);
      }
    }
  }, []);
  QUICKWIT_APPENDER.addExtraProperty("user_agent", navigator.userAgent);
};
